/**
 * PatientStatusBadge.tsx — patient-facing status pill (no SCREAMING uppercase).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient status copy
 * normalisation.
 *
 * WHY: kit `Badge` uppercases its children (staff queues rely on that). Patient
 * surfaces pass sentence-case labels from `patientStatusCopy` and must keep
 * that casing for readability and screen readers. Colour is secondary — the
 * label (and optional detail) carry the meaning.
 */
import type { CSSProperties } from 'react';
import type { BadgeTone } from './kit';
import type { PatientStatusCopy } from '../lib/patientStatusCopy';

const TONE_STYLES: Record<BadgeTone, { bg: string; text: string }> = {
  neutral: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  positive: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  pending: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' },
  muted: { bg: 'var(--gps-surface-2)', text: 'var(--gps-muted)' },
  danger: { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' },
};

const pillStyle = (tone: BadgeTone): CSSProperties => {
  const t = TONE_STYLES[tone];
  return {
    display: 'inline-block',
    background: t.bg,
    color: t.text,
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.01em',
    textTransform: 'none',
    padding: '4px 10px',
    borderRadius: 'var(--gps-r-pill)',
    lineHeight: 1.3,
  };
};

/**
 * Renders {@link PatientStatusCopy} as a readable pill (+ optional detail line).
 * Label text (not colour alone) carries the meaning for assistive tech.
 */
export function PatientStatusBadge({
  status,
  showDetail = false,
}: {
  status: PatientStatusCopy;
  /** When true and `status.detail` is set, show the detail under the pill. */
  showDetail?: boolean;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '2px',
        maxWidth: '100%',
      }}
    >
      <span style={pillStyle(status.tone)}>{status.label}</span>
      {showDetail && status.detail ? (
        <span style={{ fontSize: '12px', color: 'var(--gps-muted)', textAlign: 'right' }}>{status.detail}</span>
      ) : null}
    </span>
  );
}
